#!/usr/bin/env node
/**
 * VibeCheck CLI v2 — Entry Point
 * 
 * - No args → interactive menu
 * - With args → Commander dispatch
 * - Zero auth gates, zero friction
 */

import { createProgram } from './cli';
import { colors, icons } from './ui/theme';

async function main(): Promise<void> {
  const args = process.argv.slice(2);

  // If no args and TTY, show interactive menu
  if (args.length === 0 && process.stdin.isTTY && !process.env.CI) {
    try {
      const { showInteractiveMenu } = await import('./ui/menu');
      const command = await showInteractiveMenu();
      // Re-run with selected command
      process.argv.push(command);
      const program = createProgram();
      await program.parseAsync(process.argv);
    } catch (err: any) {
      // If user cancels (Ctrl+C), exit gracefully
      if (err?.message?.includes('User force closed')) {
        console.log(`\n${colors.dim('Cancelled.')}`);
        process.exit(0);
      }
      // Fallback to help if menu fails
      const program = createProgram();
      program.outputHelp();
    }
    return;
  }

  // Normal Commander dispatch
  const program = createProgram();

  // Handle errors gracefully
  program.exitOverride();

  try {
    await program.parseAsync(process.argv);
  } catch (err: any) {
    // Commander throws on --help and --version, which is fine
    if (err.code === 'commander.helpDisplayed' || err.code === 'commander.version') {
      process.exit(0);
    }

    // Unknown command
    if (err.code === 'commander.unknownCommand') {
      const cmd = args[0];
      console.error(`\n${colors.error(icons.error)} Unknown command: ${colors.warning(cmd)}`);
      console.error(`\n${colors.dim('Run')} ${colors.info('vibecheck --help')} ${colors.dim('for available commands.')}\n`);
      process.exit(3);
    }

    // Other errors
    if (!process.exitCode) {
      console.error(`\n${colors.error(icons.error)} ${err.message || 'An unexpected error occurred'}`);
      if (process.env.VIBECHECK_DEBUG === 'true' && err.stack) {
        console.error(colors.dim(err.stack));
      }
      process.exit(3);
    }
  }
}

// Graceful shutdown
process.on('SIGINT', () => {
  console.log(`\n${colors.dim('Interrupted')}`);
  process.exit(130);
});

process.on('SIGTERM', () => {
  process.exit(143);
});

process.on('uncaughtException', (error) => {
  console.error(`\n${colors.error(icons.error)} Uncaught error: ${error.message}`);
  if (process.env.VIBECHECK_DEBUG === 'true') {
    console.error(colors.dim(error.stack || ''));
  }
  process.exit(1);
});

process.on('unhandledRejection', (reason) => {
  console.error(`\n${colors.error(icons.error)} Unhandled rejection: ${reason}`);
  process.exit(1);
});

main();
